import { orbitBasis, type OrbitState } from "../../sim/kepler";
import type { ScaleMapper } from "../scale-mapper";

/** Bodies further than this from their coarse chord, in pixels, get an arc. */
const REFINE_ABOVE_PX = 1;
/** Largest on-screen sagitta an arc's chords may have, in pixels. */
export const ARC_TOLERANCE_PX = 0.5;
/** Arc vertices on each side of the body. */
export const MAX_ARC_STEPS = 64;
/** Vertices an arc can hold: both sides, the body and the two coarse ends. */
export const ARC_CAPACITY = 2 * MAX_ARC_STEPS + 3;
const MIN_STEP_FRACTION = 1 / 4096; // of the coarse step, so a camera on the line cannot stall the march

/** A dense run of the orbit around a body, joined to the coarse loop at both ends. */
export interface Arc {
  /** Vertices in use, both coarse ends included. */
  count: number;
  /** Mapped positions relative to the parent, 3 per vertex, from the kLo end to the kHi end. */
  readonly positions: Float64Array;
  /** Eccentric anomaly of each vertex, unwrapped around the body's. */
  readonly anomalies: Float64Array;
  /** Coarse vertex at each end, unwrapped (kLo < kHi); take them modulo the loop size to index it. */
  kLo: number;
  kHi: number;
}

export function createArc(): Arc {
  return {
    count: 0,
    positions: new Float64Array(3 * ARC_CAPACITY),
    anomalies: new Float64Array(ARC_CAPACITY),
    kLo: 0,
    kHi: 0,
  };
}

// Math.hypot guards against overflow these magnitudes never reach, at several times the cost
const len3 = (x: number, y: number, z: number): number => Math.sqrt(x * x + y * y + z * z);

/** Copies the xyz triple at vertex i of `from` to vertex j of `to`. */
function copy3(from: ArrayLike<number>, i: number, to: Float64Array, j: number): void {
  to[3 * j] = from[3 * i];
  to[3 * j + 1] = from[3 * i + 1];
  to[3 * j + 2] = from[3 * i + 2];
}

/** Pixels per scene unit at distance 1 from the camera. */
export function pixelsPerUnit(viewportHeight: number, fovRad: number): number {
  return viewportHeight / 2 / Math.tan(fovRad / 2);
}

/** Maps an offset in km from the parent into scene units along the same direction. */
export function mapOffset(
  mapper: ScaleMapper,
  x: number,
  y: number,
  z: number,
  out: Float64Array | Float32Array,
  o: number,
): void {
  const len = len3(x, y, z);
  const s = len === 0 ? 0 : mapper.mapDistance(len) / len;
  out[o] = x * s;
  out[o + 1] = y * s;
  out[o + 2] = z * s;
}

/** Calls fn(start, length) for the one or two runs that cover `count` slots from `start` around a ring of n. */
export function forEachRun(
  start: number,
  count: number,
  n: number,
  fn: (start: number, length: number) => void,
): void {
  const s = ((start % n) + n) % n;
  const first = Math.min(count, n - s);
  if (first > 0) fn(s, first);
  if (count > first) fn(0, count - first);
}

/**
 * Pixels between point p and the coarse chord from vertex ka to kb, seen from cam at p's distance.
 * All positions share one frame, usually relative to the parent; 0 when p is at the camera.
 */
export function coarseGapPx(
  p: ArrayLike<number>,
  coarse: ArrayLike<number>,
  ka: number,
  kb: number,
  cam: ArrayLike<number>,
  ppu: number,
): number {
  const ax = coarse[3 * ka],
    ay = coarse[3 * ka + 1],
    az = coarse[3 * ka + 2];
  const dx = coarse[3 * kb] - ax,
    dy = coarse[3 * kb + 1] - ay,
    dz = coarse[3 * kb + 2] - az;
  const px = p[0] - ax,
    py = p[1] - ay,
    pz = p[2] - az;
  const len2 = dx * dx + dy * dy + dz * dz;
  const t = len2 === 0 ? 0 : Math.min(Math.max((px * dx + py * dy + pz * dz) / len2, 0), 1);
  const off = len3(px - t * dx, py - t * dy, pz - t * dz);
  const depth = len3(p[0] - cam[0], p[1] - cam[1], p[2] - cam[2]);
  return depth === 0 ? 0 : (off / depth) * ppu;
}

/** Writes the slots whose gap exceeds REFINE_ABOVE_PX into out, largest gap first; returns how many. */
export function selectRefinements(gaps: ArrayLike<number>, out: Int32Array): number {
  if (out.length === 0) return 0;
  let count = 0;
  for (let n = 0; n < gaps.length; n++) {
    const g = gaps[n];
    if (!(g > REFINE_ABOVE_PX)) continue;
    if (count === out.length && g <= gaps[out[count - 1]]) continue;
    let j = count < out.length ? count++ : count - 1;
    while (j > 0 && gaps[out[j - 1]] < g) {
      out[j] = out[j - 1];
      j--;
    }
    out[j] = n;
  }
  return count;
}

// orbit plane of the arc being built: r(E) = a (cos E - e) P + b sin E Q, so each point costs two trig calls
const plane = new Float64Array(3); // a, e, b
const axes = new Float64Array(6); // P xyz, Q xyz
const probeA = new Float64Array(3),
  probeB = new Float64Array(3),
  current = new Float64Array(3);
const sideE = new Float64Array(MAX_ARC_STEPS); // one side's anomalies, nearest the body first
const sidePos = new Float64Array(3 * MAX_ARC_STEPS);

function setPlane(s: OrbitState): void {
  plane[0] = s.a;
  plane[1] = s.e;
  plane[2] = s.a * Math.sqrt(1 - s.e * s.e);
  orbitBasis(s, axes);
}

function mappedAt(mapper: ScaleMapper, E: number, out: Float64Array, o: number): void {
  const xp = plane[0] * (Math.cos(E) - plane[1]),
    yp = plane[2] * Math.sin(E);
  mapOffset(
    mapper,
    xp * axes[0] + yp * axes[3],
    xp * axes[1] + yp * axes[4],
    xp * axes[2] + yp * axes[5],
    out,
    o,
  );
}

/**
 * Anomaly step from E, whose mapped position is `at`, that keeps the chord within
 * ARC_TOLERANCE_PX on screen; coarseStep or more once the coarse loop suffices.
 */
function stepAt(
  mapper: ScaleMapper,
  E: number,
  at: Float64Array,
  coarseStep: number,
  cam: ArrayLike<number>,
  ppu: number,
): number {
  mappedAt(mapper, E - coarseStep / 2, probeA, 0);
  mappedAt(mapper, E + coarseStep / 2, probeB, 0);
  // sagitta of the coarse-length chord centred on E, and how far the camera is from it
  const sag =
    len3(
      probeA[0] + probeB[0] - 2 * at[0],
      probeA[1] + probeB[1] - 2 * at[1],
      probeA[2] + probeB[2] - 2 * at[2],
    ) / 2;
  const d = len3(at[0] - cam[0], at[1] - cam[1], at[2] - cam[2]);
  const gap = d === 0 ? Infinity : (sag * ppu) / d;
  if (gap <= ARC_TOLERANCE_PX) return coarseStep;
  // sagitta grows with the square of the step
  return Math.max(coarseStep * Math.sqrt(ARC_TOLERANCE_PX / gap), coarseStep * MIN_STEP_FRACTION);
}

/** Marches from E0 in direction dir, filling sideE and sidePos; returns the vertex count. */
function march(
  mapper: ScaleMapper,
  E0: number,
  dir: number,
  coarseStep: number,
  maxSpan: number,
  cam: ArrayLike<number>,
  ppu: number,
): number {
  let E = E0,
    n = 0;
  mappedAt(mapper, E0, current, 0);
  while (n < MAX_ARC_STEPS) {
    const h = stepAt(mapper, E, current, coarseStep, cam, ppu);
    if (h >= coarseStep) break;
    E += dir * h;
    if (Math.abs(E - E0) >= maxSpan) break;
    mappedAt(mapper, E, current, 0);
    sideE[n] = E;
    copy3(current, 0, sidePos, n);
    n++;
  }
  return n;
}

function copyCoarse(
  coarse: ArrayLike<number>,
  k: number,
  segments: number,
  out: Float64Array,
  v: number,
): void {
  copy3(coarse, ((k % segments) + segments) % segments, out, v);
}

/**
 * Fills out with a dense arc around the body at eccentric anomaly E0 (radians, in [0, 2pi)).
 * `coarse` is the mapped loop of `segments` vertices relative to the parent, evenly spaced in
 * eccentric anomaly; `body` and `cam` are relative to the parent too. Steps keep each chord's
 * on-screen sagitta within ARC_TOLERANCE_PX. The arc ends on coarse vertices and never covers
 * more than segments - 1 coarse segments, so part of the loop always stays visible.
 */
export function refineArc(
  s: OrbitState,
  E0: number,
  mapper: ScaleMapper,
  coarse: ArrayLike<number>,
  segments: number,
  body: ArrayLike<number>,
  cam: ArrayLike<number>,
  ppu: number,
  out: Arc,
): void {
  const coarseStep = (2 * Math.PI) / segments;
  const maxSpan = (segments / 2 - 2) * coarseStep;
  const { positions, anomalies } = out;
  setPlane(s);
  let v = 1; // vertex 0 is the kLo end, written last
  const left = march(mapper, E0, -1, coarseStep, maxSpan, cam, ppu);
  for (let j = left - 1; j >= 0; j--, v++) {
    anomalies[v] = sideE[j];
    copy3(sidePos, j, positions, v);
  }
  anomalies[v] = E0;
  positions[3 * v] = body[0];
  positions[3 * v + 1] = body[1];
  positions[3 * v + 2] = body[2];
  v++;
  const right = march(mapper, E0, 1, coarseStep, maxSpan, cam, ppu);
  for (let j = 0; j < right; j++, v++) {
    anomalies[v] = sideE[j];
    copy3(sidePos, j, positions, v);
  }
  out.kLo = Math.ceil(anomalies[1] / coarseStep) - 1;
  out.kHi = Math.floor(anomalies[v - 1] / coarseStep) + 1;
  anomalies[0] = out.kLo * coarseStep;
  copyCoarse(coarse, out.kLo, segments, positions, 0);
  anomalies[v] = out.kHi * coarseStep;
  copyCoarse(coarse, out.kHi, segments, positions, v);
  out.count = v + 1;
}
